import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Send, MessageSquareText, Image as ImageIcon, Loader2, X, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { standardizeImage } from "@/lib/imageResize";
import { useLang } from "@/context/LangContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function ChatList({ chats, currentId, onArchive, onDelete, archived, t }) {
  return (
    <div className="space-y-1 w-full min-w-0">
      {chats.map((c) => (
        <div
          key={c.chat_id}
          className={`flex items-center gap-2 p-2 pr-2 rounded-xl w-full min-w-0 transition-colors ${
            currentId === c.chat_id ? "bg-primary/10" : "hover:bg-muted"
          }`}
        >
          <Link
            to={`/chat/${c.chat_id}`}
            data-testid={`chat-list-item-${c.chat_id}`}
            className="flex items-center gap-3 flex-1 min-w-0"
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={c.vendor_logo} />
              <AvatarFallback>{c.business_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{c.business_name}</div>
              <div className="text-xs text-muted-foreground truncate">{c.last_message || t("chat.say_hello")}</div>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
            onClick={(e) => { e.preventDefault(); onArchive(c.chat_id); }}
            data-testid={`chat-${archived ? "unarchive" : "archive"}-${c.chat_id}`}
            aria-label={archived ? t("chat.restore") : t("chat.archive")}
            title={archived ? t("chat.restore") : t("chat.archive")}
          >
            {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                data-testid={`chat-delete-btn-${c.chat_id}`}
                aria-label="Delete conversation"
                title="Delete conversation"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={`chat-delete-dialog-${c.chat_id}`}>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
                <AlertDialogDescription>
                  The chat with <strong>{c.business_name}</strong> will be removed from your list.
                  The other party still sees it. If they send you a new message, the thread will re-appear.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`chat-delete-cancel-${c.chat_id}`}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(c.chat_id)}
                  data-testid={`chat-delete-confirm-${c.chat_id}`}
                >
                  Delete for me
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ))}
      {chats.length === 0 && (
        <p className="p-4 text-sm text-muted-foreground" data-testid="chat-list-empty">
          {archived ? t("chat.empty_archived") : t("chat.empty_active")}
        </p>
      )}
    </div>
  );
}

export default function ChatPage() {
  const { chatId } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [tab, setTab] = useState("active");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [pendingImage, setPendingImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [chatMeta, setChatMeta] = useState(null);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const fileRef = useRef(null);

  const loadChats = async (which = tab) => {
    const url = which === "archived" ? "/chats?archived=true" : "/chats";
    const { data } = await api.get(url);
    setChats(data);
  };

  useEffect(() => { loadChats(tab); /* eslint-disable-next-line */ }, [tab]);

  const archiveChat = async (cid) => {
    try {
      const action = tab === "archived" ? "unarchive" : "archive";
      await api.post(`/chats/${cid}/${action}`);
      toast.success(action === "archive" ? t("chat.archived_toast") : t("chat.restored_toast"));
      if (chatId === cid) navigate("/chat", { replace: true });
      await loadChats(tab);
    } catch (e) {
      toast.error(t("chat.update_failed"));
    }
  };

  const deleteChat = async (cid) => {
    try {
      await api.delete(`/chats/${cid}`);
      toast.success("Conversation deleted for you");
      if (chatId === cid) navigate("/chat", { replace: true });
      await loadChats(tab);
    } catch (e) {
      toast.error("Could not delete conversation");
    }
  };

  useEffect(() => {
    if (!chatId) return;
    const meta = chats.find((c) => c.chat_id === chatId);
    setChatMeta(meta || null);

    let ws = null;
    let pollTimer = null;
    let stopped = false;

    const loadAndMarkRead = async () => {
      try {
        const { data } = await api.get(`/chats/${chatId}/messages`);
        if (!stopped) setMessages(data);
      } catch (_) { /* ignore */ }
    };

    // Try WebSocket first
    try {
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "");
      const url = `${proto}://${base}/api/ws/chat/${chatId}`;
      ws = new WebSocket(url);
      ws.onopen = () => loadAndMarkRead();
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "message" && payload.message) {
            setMessages((prev) => {
              if (prev.find((m) => m.message_id === payload.message.message_id)) return prev;
              return [...prev, payload.message];
            });
          } else if (payload.type === "read") {
            setMessages((prev) =>
              prev.map((m) => (m.sender_id === user?.user_id ? { ...m, read: true } : m))
            );
          }
        } catch (_) { /* ignore */ }
      };
      ws.onerror = () => {
        if (!pollTimer) pollTimer = setInterval(loadAndMarkRead, 4000);
      };
      ws.onclose = () => {
        if (!stopped && !pollTimer) pollTimer = setInterval(loadAndMarkRead, 4000);
      };
    } catch (e) {
      pollTimer = setInterval(loadAndMarkRead, 4000);
    }

    wsRef.current = ws;
    loadAndMarkRead();

    return () => {
      stopped = true;
      if (pollTimer) clearInterval(pollTimer);
      try { ws && ws.close(); } catch (_) { /* ignore */ }
    };
  }, [chatId, chats, user?.user_id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await processIncomingImage(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const processIncomingImage = async (rawFile) => {
    if (!rawFile) return;
    if (!/^image\//.test(rawFile.type)) {
      toast.error(t("chat.image_only"));
      return;
    }
    setUploading(true);
    try {
      const f = await standardizeImage(rawFile);
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingImage(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("chat.upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  const onPaste = async (ev) => {
    const items = ev.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === "file" && /^image\//.test(it.type)) {
        const file = it.getAsFile();
        if (file) {
          ev.preventDefault();
          await processIncomingImage(file);
          return;
        }
      }
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!chatId) return;
    if (!text.trim() && !pendingImage) return;
    const payload = { text: text.trim() || (pendingImage ? "[image]" : ""), image_url: pendingImage || null };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "message", ...payload }));
        setText("");
        setPendingImage("");
        loadChats("active").catch(() => {});
        return;
      } catch (_) { /* ignore, fall through to HTTP */ }
    }
    try {
      const { data } = await api.post(`/chats/${chatId}/messages`, payload);
      setMessages((m) => [...m, data]);
      setText("");
      setPendingImage("");
      await loadChats("active");
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-[380px_1fr] lg:grid-cols-[420px_1fr] gap-4">
      <Card className="p-2 border-border/60 h-[70vh] flex flex-col">
        <div className="p-3 border-b border-border/60 flex items-center justify-between gap-2">
          <h2 className="font-heading text-lg">{t("chat.conversations")}</h2>
        </div>
        <Tabs value={tab} onValueChange={setTab} className="px-2">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="active" data-testid="chat-tab-active">{t("chat.tab_active")}</TabsTrigger>
            <TabsTrigger value="archived" data-testid="chat-tab-archived">{t("chat.tab_archived")}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex-1 overflow-y-auto min-w-0 pr-1" data-testid="chat-list-scroll">
          <ChatList
            chats={chats}
            currentId={chatId}
            onArchive={archiveChat}
            onDelete={deleteChat}
            archived={tab === "archived"}
            t={t}
          />
        </div>
      </Card>

      <Card className="border-border/60 h-[70vh] flex flex-col">
        {!chatId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
            <MessageSquareText className="h-12 w-12" />
            <p className="text-sm">{t("chat.select_to_start")}</p>
            <Link to="/vendors" className="text-sm text-primary underline">{t("chat.browse_vendors")}</Link>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <Avatar><AvatarImage src={chatMeta?.vendor_logo} /><AvatarFallback>{chatMeta?.business_name?.[0] || "V"}</AvatarFallback></Avatar>
              <div className="flex-1">
                <div className="font-semibold">{chatMeta?.business_name || t("chat.vendor_fallback")}</div>
                <div className="text-[11px] text-muted-foreground">{t("chat.subtitle")}</div>
              </div>
              {chatMeta && (
                <Link to={`/vendors/${chatMeta.vendor_id}`} className="text-xs text-primary underline">{t("chat.view_profile")}</Link>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
              {messages.map((m) => {
                const mine = m.sender_id === user?.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl p-3 text-sm shadow-sm ${
                      mine ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-muted rounded-tl-sm"
                    }`}>
                      {m.image_url && (
                        <a href={m.image_url} target="_blank" rel="noreferrer">
                          <img
                            src={m.image_url}
                            alt=""
                            data-testid="chat-message-image"
                            className="rounded-xl max-h-60 w-full object-cover mb-1"
                          />
                        </a>
                      )}
                      {m.text && m.text !== "[image]" && <div className="whitespace-pre-wrap">{m.text}</div>}
                      <div className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {mine && (m.read ? " · read" : " · sent")}
                      </div>
                    </div>
                  </div>
                );
              })}
              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Be the first to say hello.</p>
              )}
            </div>
            {pendingImage && (
              <div className="px-3 pt-2 flex items-center gap-2 border-t border-border/60" data-testid="chat-pending-image">
                <img src={pendingImage} alt="" className="h-12 w-12 rounded-lg object-cover" />
                <span className="text-xs text-muted-foreground flex-1 truncate">{t("chat.image_ready")}</span>
                <button
                  type="button"
                  onClick={() => setPendingImage("")}
                  data-testid="chat-pending-image-clear"
                  className="h-6 w-6 rounded-full bg-muted flex items-center justify-center hover:bg-muted/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <form onSubmit={send} onPaste={onPaste} className="p-3 border-t border-border/60 flex gap-2 items-center" data-testid="chat-form">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={pickImage}
                data-testid="chat-image-input"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full shrink-0"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                data-testid="chat-image-button"
                aria-label="Attach image"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              </Button>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("chat.placeholder")}
                data-testid="chat-text-input"
              />
              <Button type="submit" disabled={!text.trim() && !pendingImage} data-testid="chat-send-button" className="rounded-full">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
